// Client Health card — compact, explainable, non-intrusive.
//
// Renders the deterministic health state, top reasons with evidence,
// and a "Why am I seeing this?" drilldown. Never shows internal fields
// that the client-facing portal must not see.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, ShieldCheck, TriangleAlert, AlertCircle, Info, ChevronDown, ChevronUp } from "lucide-react";

const STATE_META = {
  healthy:   { label: "Healthy",   Icon: ShieldCheck,   tone: "emerald",
               ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800",
               dot: "bg-emerald-500" },
  watch:     { label: "Watch",     Icon: Info,          tone: "slate",
               ring: "ring-slate-200",   bg: "bg-slate-50",   text: "text-slate-800",
               dot: "bg-slate-400" },
  attention: { label: "Attention", Icon: TriangleAlert, tone: "amber",
               ring: "ring-amber-200",   bg: "bg-amber-50",   text: "text-amber-900",
               dot: "bg-amber-500" },
  critical:  { label: "Critical",  Icon: AlertCircle,   tone: "rose",
               ring: "ring-rose-200",    bg: "bg-rose-50",    text: "text-rose-900",
               dot: "bg-rose-500" },
};

export default function ClientHealthCard({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${clientId}/intelligence`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) {
    return (
      <div data-testid="client-health-card-loading"
           className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Evaluating client health…
      </div>
    );
  }
  if (!data) return null;
  const meta = STATE_META[data.state] || STATE_META.watch;
  const Icon = meta.Icon;
  const reasons = data.reasons || [];
  const visible = showAll ? reasons : reasons.slice(0, 3);

  return (
    <div data-testid={`client-health-card-${data.state}`}
         className={`rounded-xl border border-slate-200 bg-white overflow-hidden`}>
      <div className={`px-5 py-4 flex items-center gap-3 border-b border-slate-100 ${meta.bg}`}>
        <Icon className={`w-5 h-5 ${meta.text}`} />
        <div className="flex-1 min-w-0">
          <p className={`overline mb-0.5 ${meta.text}`}>Client health</p>
          <div className="flex items-center gap-2">
            <span data-testid="client-health-state"
                  className={`font-display text-xl tracking-tight ${meta.text}`}>{meta.label}</span>
            {reasons.length > 0 && (
              <span className="text-xs text-slate-500">
                · {reasons.length} signal{reasons.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
        {data.state !== "healthy" && (
          <div className="text-xs text-slate-400" title="Deterministic score used for prioritization only">
            score {data.score}
          </div>
        )}
      </div>

      {reasons.length === 0 ? (
        <div data-testid="client-health-card-empty"
             className="px-5 py-6 text-sm text-slate-500 text-center">
          Everything looks on track.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {visible.map((r, idx) => (
            <li key={`${r.signal_type}-${idx}`}
                data-testid={`client-health-reason-${r.signal_type}`}
                className="px-5 py-3 flex items-start gap-3">
              <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                r.severity === "critical" ? "bg-rose-500" :
                r.severity === "high"     ? "bg-rose-400" :
                r.severity === "medium"   ? "bg-amber-500" :
                r.severity === "low"      ? "bg-slate-400" : "bg-slate-300"
              }`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 leading-snug">{r.text}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {r.signal_type}
                  {r.evidence_event_ids?.length ? ` · ${r.evidence_event_ids.length} evidence` : ""}
                </p>
              </div>
            </li>
          ))}
          {reasons.length > 3 && (
            <li className="px-5 py-2">
              <button data-testid="client-health-toggle"
                      onClick={() => setShowAll(v => !v)}
                      className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                {showAll ? <><ChevronUp className="w-3 h-3" /> Show less</>
                         : <><ChevronDown className="w-3 h-3" /> Show {reasons.length - 3} more</>}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
